import React from 'react';

import {
	ButtonMenuItem,
	type ButtonMenuItemProps,
	LinkMenuItem,
	type LinkMenuItemProps,
	MenuListItem,
} from '@atlaskit/navigation-system';
import {
	ExpandableMenuItem,
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/expandable-menu-item';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/flyout-menu-item';

import { ObjectListFlyout } from '../../../ui/object-list-flyout';
import { type ObjectListFlyoutProps } from '../../../ui/object-list-flyout/types';
import type {
	ExpandableItemConfig,
	FlyoutItemConfig,
	NavItemConfigInternal,
	ObjectListFlyoutItemConfig,
} from '../../types';
import { isCustomItemConfig, isObjectListFlyoutItemConfig } from '../../utils/type-guards';

import { useExpandOpenState } from './use-expand-open-state';
import { useItemDragAndDrop } from './use-item-drag-and-drop';

interface NavItemProps {
	id: string;
	item: NavItemConfigInternal;
	peek?: boolean;
	isInMoreMenu?: boolean;
	onClick?: () => void;
}

export default function NavItem({
	id,
	item,
	peek = false,
	isInMoreMenu = false,
	onClick,
}: NavItemProps): React.ReactNode {
	const { type } = item;

	const { isExpandOrFlyoutOpen, onExpandFlyoutToggle, isExpandable } = useExpandOpenState(
		item,
		peek,
	);

	const {
		draggableRef,
		isItemDraggable,
		isDragging,
		dropTargetRef,
		dropIndicator,
		maybeDragPreview,
	} = useItemDragAndDrop({
		item,
		id,
		isExpandable,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		isInMoreMenu,
		peek,
	});

	if (isCustomItemConfig(item)) {
		// If the item is a custom item, render it directly
		// TODO: Add customizationHandlers prop when ready
		return (
			<MenuListItem>
				<item.Component
					isInMoreMenu={false}
					isPeeked={peek}
					onClick={(...args) => {
						item.Component.defaultProps?.onClick?.(...args);
						onClick?.();
					}}
					hasDragIndicator={isItemDraggable ? isItemDraggable : undefined}
					draggableRef={draggableRef}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={dropTargetRef}
				/>
				{maybeDragPreview}
			</MenuListItem>
		);
	}

	if (isObjectListFlyoutItemConfig(item)) {
		const {
			type: _type,
			isOpen: _isOpen, // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in objectListFlyoutProps
			onOpenChange: _onOpenChange, // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in objectListFlyoutProps
			...objectListFlyoutProps
		} = item as ObjectListFlyoutItemConfig;
		return (
			<>
				<ObjectListFlyout
					{...objectListFlyoutProps}
					onClick={(...args) => {
						// Toggle the flyout open state
						onExpandFlyoutToggle(!isExpandOrFlyoutOpen);
						(objectListFlyoutProps as ObjectListFlyoutProps).onClick?.(...args);
						onClick?.();
					}}
					isOpen={isExpandOrFlyoutOpen}
					onOpenChange={onExpandFlyoutToggle}
					hasDragIndicator={isItemDraggable}
					triggerRef={draggableRef}
					isDragging={isDragging}
					dropIndicator={dropIndicator}
					visualContentRef={dropTargetRef}
				/>
				{maybeDragPreview}
			</>
		);
	}

	const { type: _type, elemBefore, label, testId, ...rest } = item;

	switch (type) {
		case 'link':
			// Typescript does not properly narrow the `rest` type here, so we need to use the `as` clause to cast it
			return (
				<>
					<LinkMenuItem
						{...(rest as LinkMenuItemProps)}
						elemBefore={elemBefore}
						onClick={(...args) => {
							(rest as LinkMenuItemProps).onClick?.(...args);
							onClick?.();
						}}
						testId={testId}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					>
						{label}
					</LinkMenuItem>
					{maybeDragPreview}
				</>
			);
		case 'button':
			return (
				<>
					<ButtonMenuItem
						{...(rest as ButtonMenuItemProps)}
						elemBefore={elemBefore}
						onClick={(...args) => {
							(rest as ButtonMenuItemProps).onClick?.(...args);
							onClick?.();
						}}
						testId={testId}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					>
						{label}
					</ButtonMenuItem>
					{maybeDragPreview}
				</>
			);
		case 'expand': {
			const {
				isDefaultExpanded: _isDefaultExpanded, // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
				isExpanded: _isExpanded, // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
				onExpansionToggle: _onExpansionToggle, // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
				label,
				children,
				elemBefore,
				...triggerProps
			} = item as ExpandableItemConfig;
			return (
				<>
					<ExpandableMenuItem
						isExpanded={isExpandOrFlyoutOpen}
						onExpansionToggle={onExpandFlyoutToggle}
						dropIndicator={dropIndicator}
						ref={dropTargetRef}
					>
						<ExpandableMenuItemTrigger
							{...triggerProps}
							onClick={(...args) => {
								triggerProps?.onClick?.(...args);
								onClick?.();
							}}
							testId={testId}
							// During drags, custom elemBefore is not shown
							// https://atlassian.design/components/navigation-system/side-nav-items/drag-and-drop#drag-start
							elemBefore={isDragging ? null : elemBefore}
							hasDragIndicator={isItemDraggable}
							ref={draggableRef}
							isDragging={isDragging}
						>
							{label}
						</ExpandableMenuItemTrigger>
						<ExpandableMenuItemContent>{children}</ExpandableMenuItemContent>
					</ExpandableMenuItem>
					{maybeDragPreview}
				</>
			);
		}
		case 'flyout': {
			const {
				isDefaultOpen: _isDefaultOpen, // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
				isOpen: _isOpen, // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
				onOpenChange: _onOpenChange, // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
				label,
				children,
				...triggerProps
			} = item as FlyoutItemConfig;
			return (
				<>
					<FlyoutMenuItem isOpen={isExpandOrFlyoutOpen}>
						<FlyoutMenuItemTrigger
							{...triggerProps}
							elemBefore={elemBefore}
							onClick={(...args) => {
								// Toggle the flyout open state
								onExpandFlyoutToggle(!isExpandOrFlyoutOpen);
								triggerProps?.onClick?.(...args);
								onClick?.();
							}}
							testId={testId}
							hasDragIndicator={isItemDraggable}
							ref={draggableRef}
							isDragging={isDragging}
							dropIndicator={dropIndicator}
							visualContentRef={dropTargetRef}
						>
							{label}
						</FlyoutMenuItemTrigger>
						<FlyoutMenuItemContent>{children}</FlyoutMenuItemContent>
					</FlyoutMenuItem>
					{maybeDragPreview}
				</>
			);
		}
		default:
			return null;
	}
}
